<template>
  <v-container class="dk-page">
    <!-- Hero -->
    <section class="dk-hero">
      <div class="dk-hero__body">
        <p class="dk-eyebrow">Neu für Ihre Praxis · kostenlos inkl. Versand</p>
        <h1 class="dk-hero__title">
          Die neuen WaizmannTabellen {{ baseStore.thisYear }}<br />
          <span class="text-green">als Komplettpaket</span> <sup class="dk-badge">Neu</sup>
        </h1>
        <p class="dk-hero__text">
          Drei Tabellen, eine Antwort auf jede Patientenfrage: Erwachsene, 60plus und KFO/Kind –
          die Top-Tarife der Zahnzusatzversicherung im praktischen Postkartenformat.
          Zum Mitnehmen aus dem Wartezimmer, zum Weitergeben am Empfang.
          <b class="dk-new">Neu dabei: die WaizmannTabelle 60plus</b> – mit Tarifen, die bis über <strong>GOZ-Faktor 5</strong> leisten.
        </p>
        <div class="dk-hero__facts">
          <span class="dk-fact"><v-icon size="18" color="green">mdi-check-circle</v-icon> Kostenlos für Zahnarztpraxen</span>
          <span class="dk-fact"><v-icon size="18" color="green">mdi-check-circle</v-icon> Versand inklusive</span>
          <span class="dk-fact"><v-icon size="18" color="green">mdi-check-circle</v-icon> Das Original seit 1995</span>
        </div>
        <v-btn color="primary" size="large" class="dk-btn mt-4" @click="scrollToOrder">
          <v-icon start>play_arrow</v-icon>
          Jetzt kostenlos anfordern
        </v-btn>
      </div>
      <div class="dk-hero__tiles" aria-hidden="true">
        <TableTile v-for="id in packageTileIds" :key="id" v-bind="tileProps(id)" />
      </div>
    </section>

    <!-- Bestellbox -->
    <section ref="orderRef" class="dk-section dk-order">
      <v-expand-transition>
        <div v-if="lastOrder" class="dk-confirm">
          <div class="dk-confirm__head">
            <v-icon color="green" size="32">mdi-check-circle</v-icon>
            <div>
              <h2 class="dk-confirm__title">Vielen Dank – Ihre Anforderung ist eingegangen.</h2>
              <p class="dk-card__text">Wir senden Ihnen in den nächsten Tagen kostenlos zu:</p>
            </div>
          </div>
          <div class="dk-chips">
            <v-chip v-for="item in lastOrder.items" :key="item" color="green" variant="tonal" size="small" class="dk-chip">
              {{ item }}
            </v-chip>
          </div>
          <p class="dk-card__text mt-3">
            Weitere WaizmannTabellen und Aufsteller finden Sie jederzeit unter
            <router-link to="/waizmanntabellen_bestellen">WaizmannTabellen anfordern</router-link>.
          </p>
        </div>
      </v-expand-transition>

      <template v-if="!lastOrder">
        <p class="dk-eyebrow">Jetzt kostenlos anfordern</p>
        <h2 class="dk-section__title">Ihre Auswahl</h2>

        <div class="dk-options">
          <label v-for="option in options" :key="option.key" :class="['dk-option', { 'dk-option--active': selected.includes(option.key) }]">
            <v-checkbox-btn :model-value="selected.includes(option.key)" color="green" @update:modelValue="() => toggleOption(option.key)" />
            <div class="dk-option__body">
              <span class="dk-option__title">
                {{ option.title }}
                <sup v-if="option.badge" class="dk-badge">{{ option.badge }}</sup>
              </span>
              <span class="dk-option__text">{{ option.text }}</span>
            </div>
            <TableTile v-if="option.tileId" size="sm" v-bind="tileProps(option.tileId)" />
          </label>
        </div>

        <div v-if="userData" class="dk-address">
          <p class="dk-eyebrow">Versandadresse</p>
          <p class="dk-address__line">
            {{ userData.user_name }}<span v-if="userData.user_strasse">, {{ userData.user_strasse }} {{ userData.user_hsnr }}</span><span v-if="userData.user_ort">, {{ userData.user_plz }} {{ userData.user_ort }}</span>
          </p>
          <p v-if="!userData.user_strasse || !userData.user_plz || !userData.user_ort" class="text-red dk-address__warn">
            Bitte ergänzen Sie zuerst Ihre Adresse.
          </p>
          <a @click="showUserUpdate = true">Adresse ändern</a>
        </div>
        <div v-else class="dk-address">
          <p class="dk-card__text">
            <template v-if="loginFailed">
              Ihr Anmeldelink ist leider nicht mehr gültig.
              <router-link to="/login">Bitte melden Sie sich an</router-link>, um anzufordern.
            </template>
            <template v-else-if="route.params.token">Sie werden angemeldet …</template>
            <template v-else>
              Bitte <router-link to="/login">melden Sie sich an</router-link>, um kostenlos anzufordern.
            </template>
          </p>
        </div>

        <v-btn
          color="primary"
          size="x-large"
          class="dk-btn mt-4"
          :disabled="!userData || selected.length === 0"
          @click="startNeueBestellung"
        >
          <v-icon start>play_arrow</v-icon>
          {{ orderLabel }}
        </v-btn>
        <p class="dk-fineprint">Kostenlos für Zahnarztpraxen, inklusive Versand. Kein Abo, keine Folgekosten.</p>
      </template>
    </section>

    <!-- Inhalt des Pakets -->
    <section class="dk-section">
      <p class="dk-eyebrow">Das steckt im Komplettpaket</p>
      <h2 class="dk-section__title">Drei Tabellen für drei Patientengruppen</h2>
      <div class="dk-grid">
        <article v-for="item in packageItems" :key="item.id" class="dk-card">
          <div class="dk-card__top">
            <img :src="item.image" :alt="item.title" class="dk-card__picto" />
            <TableTile size="sm" v-bind="tileProps(item.id)" />
          </div>
          <h3 class="dk-card__title">
            {{ item.title }}
            <sup v-if="item.badge" class="text-green">{{ item.badge }}</sup>
          </h3>
          <p class="dk-card__text" v-html="item.text"></p>
        </article>
      </div>
    </section>

    <!-- 3-fach Design-Aufsteller -->
    <section class="dk-section dk-stand">
      <div class="dk-stand__media">
        <img :src="standImage" alt="3-fach Design-Aufsteller mit den drei WaizmannTabellen" />
      </div>
      <div class="dk-stand__body">
        <p class="dk-eyebrow">Passend dazu</p>
        <h2 class="dk-section__title">Der 3-fach Design-Aufsteller <sup class="dk-badge">Neu</sup></h2>
        <p class="dk-card__text">
          Alle drei Tabellen griffbereit und aufgeräumt an einem Platz: Der neue Design-Aufsteller
          präsentiert Erwachsene, 60plus und KFO/Kind nebeneinander – im Wartezimmer, am Empfang
          oder im Beratungsraum. Hochwertig, standfest und natürlich ebenfalls kostenlos.
        </p>
        <ul class="dk-list">
          <li>Drei Fächer für die drei WaizmannTabellen</li>
          <li>Dezentes Design, passt in jede Praxis</li>
          <li>Kostenlos inklusive Versand</li>
        </ul>
      </div>
    </section>

    <v-dialog v-model="showUserUpdate" width="888">
      <v-card><v-card-text><AccountView /></v-card-text></v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { inject, ref, reactive, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import AccountView from '@/components/objects/user/AccountView.vue'
import TableTile from '@/components/sites/objects/TableTile.vue'
import { PRODUCT_KEYS, PRODUCT_ORDER } from '@/constants/bestellProdukte'

const baseCalls = inject('baseCalls')
const loaderStore = inject('loaderStore')
const errorStore = inject('errorStore')
const userStore = inject('userStore')
const baseStore = inject('baseStore')
const updateDocumentHead = inject('updateDocumentHead')
const { userData } = storeToRefs(userStore)
const route = useRoute()

const showUserUpdate = ref(false)
const loginFailed = ref(false)
const lastOrder = ref(null)
const orderRef = ref(null)

const packageTileIds = ['erwachsene', '60plus', 'kinder']

function tileProps(id) {
  const tiles = Array.isArray(baseStore.waizmannTableTiles) ? baseStore.waizmannTableTiles : []
  const tile = tiles.find((item) => item.id === id) || {}
  return { number: tile.number || '', subtitle: tile.label || tile.subtitle || '', color: tile.color || '#3166a5', ink: tile.ink || 'light' }
}

const standImage = new URL('../../../assets/bestellung/dreifachaufsteller.jpg', import.meta.url).href

const packageItems = [
  {
    id: 'erwachsene',
    title: 'WaizmannTabelle Erwachsene',
    text: 'Die Top-Tarife für Erwachsene – klar bewertet nach RealWert, Leistung und Beitrag.',
    image: new URL('../../../assets/bestellung/togo_e_picto.png', import.meta.url).href,
  },
  {
    id: '60plus',
    title: 'WaizmannTabelle 60plus',
    text: 'Neu: die besten Tarife für Patienten ab 60 – ohne Altersgrenze, mit starken Zahnersatz-Leistungen und Tarifen, die bis über <strong>GOZ-Faktor 5</strong> leisten.',
    image: new URL('../../../assets/bestellung/togo_60plus_picto.png', import.meta.url).href,
    badge: 'Neu',
  },
  {
    id: 'kinder',
    title: 'WaizmannTabelle KFO/Kind',
    text: 'Die wichtigsten Kindertarife für Kieferorthopädie und Zahnbehandlung – für die Familienberatung.',
    image: new URL('../../../assets/bestellung/togo_k_picto.png', import.meta.url).href,
  },
]

const options = [
  { key: PRODUCT_KEYS.PACKAGE, title: 'Komplettpaket: Erwachsene + 60plus + KFO/Kind', badge: 'Neu', text: 'Die drei neuen WaizmannTabellen im Postkartenformat.', tileId: null },
  { key: PRODUCT_KEYS.STAND_TRIPLE, title: '3-fach Design-Aufsteller', badge: 'Neu', text: 'Präsentiert alle drei Tabellen nebeneinander.', tileId: null },
  { key: PRODUCT_KEYS.PZR, title: 'WaizmannTabelle Prophylaxe dazu', text: 'Top-Tarife mit PZR-Flatrate – ideal am Prophylaxe-Platz.', tileId: 'pzr' },
  { key: PRODUCT_KEYS.STAND_PZR, title: 'Design-Aufsteller Prophylaxe dazu', text: 'Der passende Aufsteller für die Prophylaxe-Tabelle.', tileId: null },
]

// Vorauswahl: Komplettpaket + 3-fach Aufsteller
const selected = ref([PRODUCT_KEYS.PACKAGE, PRODUCT_KEYS.STAND_TRIPLE])

function toggleOption(key) {
  selected.value = selected.value.includes(key) ? selected.value.filter((k) => k !== key) : [...selected.value, key]
}

const orderedSelection = computed(() => PRODUCT_ORDER.filter((key) => selected.value.includes(key)))

const orderLabel = computed(() => {
  const n = orderedSelection.value.length
  if (n === 0) return 'Bitte Auswahl treffen'
  if (n === 1) return 'Jetzt kostenlos anfordern'
  return `Alle ${n} Positionen jetzt kostenlos anfordern`
})

const bestellung = reactive({
  bestell_status: 0,
  bestell_comment: '',
  bestell_produkt_text: '',
  bestell_kategorie: 0,
  action: 'neueBestellung',
})

function scrollToOrder() {
  orderRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

async function startLogin() {
  loaderStore.setLoader(true, 'Bitte warten, Sie werden eingeloggt...')
  try {
    const response = await baseCalls.userPost({ action: 'startlogin', token: route.params.token })
    if (response.data.user) {
      userStore.setUser(response.data.user)
    } else {
      loginFailed.value = true
    }
  } catch (err) {
    loginFailed.value = true
  } finally {
    loaderStore.setLoader(false)
  }
}

async function startNeueBestellung() {
  if (!userData.value || orderedSelection.value.length === 0) return
  bestellung.bestell_produkt_text = orderedSelection.value.join(', ')
  loaderStore.setLoader(true)
  try {
    const response = await baseCalls.bestellungPost(bestellung)
    if (response.data.success) {
      lastOrder.value = { items: orderedSelection.value }
      scrollToOrder()
    } else {
      errorStore.setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')
    }
  } catch (err) {
    errorStore.setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')
  } finally {
    loaderStore.setLoader(false)
  }
}

onMounted(() => {
  if (!userData.value && route.params.token) {
    startLogin()
  }
  setTimeout(() => userStore.setLoginView(false), 1000)
  updateDocumentHead(
    'Die neuen WaizmannTabellen als Komplettpaket | kostenlos anfordern',
    'Erwachsene, 60plus und KFO/Kind als Komplettpaket plus 3-fach Design-Aufsteller – kostenlos für Zahnarztpraxen.'
  )
})
</script>

<style scoped>
.dk-page {
  max-width: 1040px;
  --tile-w: 112px;
}

.dk-eyebrow {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: #6b7a99;
  font-weight: 600;
  margin: 0 0 8px;
}

.dk-badge {
  color: #2e7d32;
  font-size: max(11px, 0.6em);
  font-weight: 800;
  letter-spacing: 0.04em;
  white-space: nowrap;
  vertical-align: super;
  margin-left: 2px;
}

.dk-new {
  color: #6f5aa8;
}

.dk-btn {
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.2px;
  border-radius: 8px;
  max-width: 100%;
  height: auto !important;
  min-height: 48px;
  padding: 10px 22px;
  white-space: normal;
}

/* Hero */
.dk-hero {
  display: flex;
  flex-direction: column;
  gap: 28px;
  background: #fff;
  border: 1px solid #e1e6ef;
  border-radius: 12px;
  padding: 32px 28px;
  margin-top: 24px;
  box-shadow: 0 16px 40px rgba(16, 42, 67, 0.06);
}

.dk-hero__body {
  flex: 1;
  min-width: 0;
}

.dk-hero__title {
  font-size: clamp(1.7rem, 3.4vw, 2.4rem);
  line-height: 1.12;
  color: #1f3a63;
  margin: 0 0 14px;
  letter-spacing: -0.3px;
}

.dk-hero__text {
  color: #4a5568;
  font-size: 1.05rem;
  line-height: 1.5;
  margin: 0 0 14px;
  max-width: 560px;
}

.dk-hero__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
}

.dk-fact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1f3a63;
  font-weight: 600;
  font-size: 0.92rem;
}

.dk-hero__tiles {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 12px 8px 4px;
}

.dk-hero__tiles .table-tile {
  box-shadow: 0 14px 32px rgba(16, 42, 67, 0.2);
}

.dk-hero__tiles .table-tile + .table-tile {
  margin-left: -8px;
}

.dk-hero__tiles .table-tile:nth-child(1) {
  transform: rotate(-9deg) translateY(10px);
  z-index: 2;
}

.dk-hero__tiles .table-tile:nth-child(2) {
  transform: translateY(-10px);
  z-index: 1;
}

.dk-hero__tiles .table-tile:nth-child(3) {
  transform: rotate(9deg) translateY(10px);
  z-index: 2;
}

/* Sektionen */
.dk-section {
  background: #f7f9fc;
  border-radius: 12px;
  padding: 28px;
  margin-top: 20px;
}

.dk-section__title {
  font-size: 1.5rem;
  color: #1f3a63;
  margin: 0 0 16px;
  line-height: 1.2;
}

.dk-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.dk-card {
  background: #fff;
  border: 1px solid #e1e6ef;
  border-radius: 10px;
  padding: 18px;
}

.dk-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.dk-card__picto {
  width: 84px;
  height: 84px;
  object-fit: contain;
}

.dk-card__title {
  color: #3166a5;
  margin: 0 0 6px;
  font-size: 1.05rem;
}

.dk-card__text {
  margin: 0;
  color: #4a5568;
  line-height: 1.45;
}

/* Aufsteller */
.dk-stand {
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #fff;
  border: 1px solid #e1e6ef;
}

.dk-stand__media {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
}

.dk-stand__media img {
  width: 100%;
  max-width: 340px;
  border-radius: 8px;
}

.dk-stand__body {
  flex: 1;
  min-width: 0;
}

.dk-list {
  margin: 14px 0 0;
  padding-left: 18px;
  color: #1f3a63;
  line-height: 1.7;
}

/* Bestellbox */
.dk-order {
  border: 2px solid #3166a5;
  background: #fff;
}

.dk-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dk-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px dashed #d5ddeb;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.dk-option :deep(.v-selection-control) {
  flex: 0 0 auto;
  min-height: 0;
}

.dk-option--active {
  border-style: solid;
  border-color: #2e7d32;
  box-shadow: 0 4px 14px rgba(46, 125, 50, 0.12);
}

.dk-option__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.dk-option__title {
  font-weight: 700;
  color: #1f3a63;
}

.dk-option__text {
  color: #4a5568;
  font-size: 0.9rem;
}

.dk-address {
  margin-top: 18px;
  padding: 14px 16px;
  background: #f7f9fc;
  border-radius: 8px;
}

.dk-address__line {
  margin: 0 0 4px;
  color: #1f3a63;
  font-weight: 600;
}

.dk-address__warn {
  margin: 4px 0;
  font-weight: 600;
}

.dk-fineprint {
  margin: 10px 0 0;
  font-size: 0.8rem;
  color: #6b7a99;
}

.dk-confirm__head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.dk-confirm__title {
  margin: 0 0 4px;
  color: #1f3a63;
  font-size: 1.35rem;
}

.dk-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.dk-chip {
  border-radius: 6px;
  font-weight: 600;
}

@media (min-width: 900px) {
  .dk-hero {
    flex-direction: row;
    align-items: center;
    padding: 40px 36px;
  }

  .dk-hero__tiles {
    flex: 0 0 380px;
  }

  .dk-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .dk-stand {
    flex-direction: row;
    align-items: center;
  }

  .dk-stand__media {
    flex: 0 0 340px;
  }
}
</style>
